import { useState, useCallback, useRef, useEffect } from 'react'

// Same viewport-triggered, throttled lazy-metadata pattern as use-doujins-metadata.ts,
// applied to Erome's lazy-load placeholders (posts past the eager-fetch limit in
// parseEromeHtml, typed "album" with a source URL but no albumImages yet).
//
// Reuses the existing /api/booru/erome/gallery route (the same one the detail modal
// calls on click) purely to learn the real item count — but only for cards that have
// actually scrolled near the viewport, throttled to a couple of concurrent requests,
// so we never burst-fetch a whole page of albums at once (that's what caused Erome's
// rate limiting in the first place).

interface EromeMetadata {
  pageCount: number
}

const metadataCache = new Map<string, EromeMetadata>()
const pendingRequests = new Map<string, Promise<EromeMetadata>>()

let activeRequests = 0
const MAX_CONCURRENT_REQUESTS = 2
const requestQueue: Array<() => void> = []

function processQueue() {
  if (activeRequests >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) {
    return
  }
  const nextRequest = requestQueue.shift()
  if (nextRequest) nextRequest()
}

export function useEromeMetadata() {
  const [metadata, setMetadata] = useState<Map<string, EromeMetadata>>(new Map())
  const observerRef = useRef<IntersectionObserver | null>(null)
  const observedElements = useRef<Map<string, HTMLElement>>(new Map())
  const observedPosts = useRef<Set<string>>(new Set())

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const postId = entry.target.getAttribute('data-post-id')
            const albumUrl = entry.target.getAttribute('data-album-url')
            if (postId && albumUrl) {
              fetchMetadata(albumUrl, postId)
            }
          }
        })
      },
      {
        rootMargin: '200px',
        threshold: 0.1,
      },
    )

    return () => {
      observerRef.current?.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchMetadata = useCallback(async (albumUrl: string, postId: string) => {
    if (metadataCache.has(albumUrl)) {
      setMetadata((prev) => new Map(prev).set(postId, metadataCache.get(albumUrl)!))
      return
    }

    if (pendingRequests.has(albumUrl)) {
      const result = await pendingRequests.get(albumUrl)!
      setMetadata((prev) => new Map(prev).set(postId, result))
      return
    }

    const promise = new Promise<EromeMetadata>((resolve, reject) => {
      const makeRequest = async () => {
        activeRequests++
        try {
          const response = await fetch(`/api/booru/erome/gallery?url=${encodeURIComponent(albumUrl)}`)
          const data = await response.json()
          const meta: EromeMetadata = { pageCount: Array.isArray(data.pages) ? data.pages.length : 0 }
          metadataCache.set(albumUrl, meta)
          pendingRequests.delete(albumUrl)
          setMetadata((prev) => new Map(prev).set(postId, meta))
          resolve(meta)
        } catch (err) {
          pendingRequests.delete(albumUrl)
          reject(err)
        } finally {
          activeRequests--
          processQueue()
        }
      }

      if (activeRequests >= MAX_CONCURRENT_REQUESTS) {
        requestQueue.push(makeRequest)
      } else {
        makeRequest()
      }
    })

    pendingRequests.set(albumUrl, promise)
  }, [])

  const observeErome = useCallback((element: HTMLElement, postId: string, albumUrl: string) => {
    if (!observerRef.current) return
    if (observedPosts.current.has(postId)) return

    element.setAttribute('data-post-id', postId)
    element.setAttribute('data-album-url', albumUrl)
    observerRef.current.observe(element)
    observedElements.current.set(postId, element)
    observedPosts.current.add(postId)

    const rect = element.getBoundingClientRect()
    const isVisible = rect.top < window.innerHeight + 200 && rect.bottom > -200
    if (isVisible) fetchMetadata(albumUrl, postId)
  }, [fetchMetadata])

  const unobserveErome = useCallback((postId: string) => {
    const element = observedElements.current.get(postId)
    if (element && observerRef.current) {
      observerRef.current.unobserve(element)
      observedElements.current.delete(postId)
      observedPosts.current.delete(postId)
    }
  }, [])

  const getEromeMetadata = useCallback((postId: string): EromeMetadata | null => {
    return metadata.get(postId) || null
  }, [metadata])

  return {
    observeErome,
    unobserveErome,
    getEromeMetadata,
  }
}
